import { formatDuration } from '@halo/utils';
import type { Msg } from '@halo/chat-sdk';
import { useMemo, useRef, useState } from 'react';

const defaultMsgExpireDuration = 7 * 24 * 60 * 60 * 1000;

const neverExpireDuration = 0;

export interface UseMessageExpireTimerProps {
  message: Msg;
}

export function useMessageExpireTimer({ message }: UseMessageExpireTimerProps) {
  // TODO amo: Date.now() use serverTime
  const timestamp = useMemo(
    () =>
      message?.expire_duration === neverExpireDuration
        ? Infinity
        : message?.read_at
          ? message?.expire_duration - (Date.now() - message?.read_at)
          : Infinity,
    [message?.expire_duration, message?.read_at]
  );

  const [time] = useSetInterval(timestamp);

  const displayTime = useMemo(() => {
    if (message?.expire_duration === neverExpireDuration) {
      return '';
    }
    return formatDuration(
      time === Infinity ? message?.expire_duration || defaultMsgExpireDuration : time
    );
  }, [message?.expire_duration, time]);

  return { displayTime, time };
}

export function useSetInterval(timestamp: number | undefined = Infinity) {
  const [time, setTime] = useState<number>(timestamp);
  const startRef = useRef(0);

  useEffect(() => {
    if (timestamp !== Infinity) {
      startRef.current = Date.now();

      const timerId = window.setInterval(() => {
        const remaining = timestamp - (Date.now() - startRef.current);

        if (remaining <= 1000) {
          window.clearInterval(timerId);
          setTime(0);
        } else {
          setTime(remaining);
        }
      }, 1000);

      return () => {
        window.clearInterval(timerId);
      };
    }
  }, [timestamp]);

  return [time] as const;
}
